import { client } from '@/shared/lib/client';
import { isInternalDomain } from '@/utils/const';

import type { ApiResponse } from './usageService';

/**
 * 订阅和账单服务API接口
 */

// 生效套餐类型定义
export interface ActivePlan {
  subscriptionId: number; // 订阅ID
  userId: string; // 用户ID
  streamlakeUserId?: string; // StreamLake用户ID
  subscriptionKind: number; // 订阅类型
  creditTotal: number; // 总额度
  creditUsed: number; // 已用额度
  creditAvailable: number; // 可用额度
  creditUnit: string; // 额度单位
  effectiveStart: number; // 生效开始时间（毫秒时间戳）
  effectiveEnd: number; // 生效结束时间（毫秒时间戳）
  autoRenew: number; // 是否自动续费（0=否, 1=是）
  subscriptionStatus: number; // 订阅状态（0=过期, 1=生效, 2=耗尽）
  paymentOrderNo: string; // 支付订单号
  planCode: string; // 套餐代码，e.g. "free", "pro_monthly", "ultra_monthly"
  planName: string; // 套餐名称，e.g. "Free Plan", "Pro Plan", "Ultra Plan"
  chargePrice: number; // 订单金额（分）
  actualPrice: number; // 实付金额（分）
  currency: string; // 货币类型，e.g. "USD"
  payMethod: string; // 支付方式，e.g. "BALANCE"
  isFirstOrder: number; // 是否首单（0=否, 1=是）
  createdAt: number; // 订单创建时间（毫秒时间戳）
  paymentOrderStatus: string; // 支付订单状态，e.g. "paid"
  cancelAutoRenewEnabled?: boolean; // 是否可以点击取消续订
}

/**
 * 查询生效套餐列表
 * @returns Promise<ApiResponse<ActivePlan[]>>
 */
export const getActivePlans = async (): Promise<ApiResponse<ActivePlan[]>> => {
  try {
    const response = (await client({
      // todo:这不敢确定不知道要不要代理
      url: '/api/proxy/api/v1/billing/activePlans',
      method: 'GET',
    })) as ApiResponse<ActivePlan[]>;
    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get active plans');
  }
};

/**
 * 订单状态枚举（后端返回 PascalCase）
 * - Submitted: 提单成功
 * - PendingPayment: 待支付
 * - Paid: 支付成功
 * - Performed: 已履约
 * - Failed: 失败
 * - Canceled: 已取消
 * - Unknown: 未知
 */
export type OrderStatus = 'Submitted' | 'PendingPayment' | 'Paid' | 'Performed' | 'Failed' | 'Canceled' | 'Unknown';

/** 单条订单信息 */
export interface Order {
  id: number;
  orderOutNo: string; // StreamLake 订单号
  streamlakeUserId: string; // StreamLake 用户 ID
  userId: string; // CF 用户 ID
  planId: number; // 套餐 ID
  planCode: string; // 套餐代码，e.g. "pro_monthly"
  planName: string; // 套餐名称，e.g. "Pro Plan"
  orderType: number; // 订单类型：1=订阅, 2=升级, 3=续期
  orderTypeDesc: string; // 订单类型描述
  amount: number; // 商品数量
  autoRenew: number; // 是否自动续费：0=否, 1=是
  orderStatus: OrderStatus; // 订单状态
  orderStatusDesc: string; // 订单状态描述
  chargePrice: number; // 订单应付金额
  actualPrice: number; // 实付金额
  discountPrice?: number; // 折扣金额
  discountType: number; // 折扣类型：1=首月折扣, 2=优惠券, 3=活动折扣, 4=其他
  isFirstPayment: number; // 是否首次支付：0=否, 1=是
  discountRate: number; // 折扣率，如 0.8 表示 8 折
  currencyUnit: string; // 币种单位，e.g. "USD"
  paymentMethod: string; // 支付方式：BALANCE | ONLINE
  subscriptionId: number; // 关联订阅 ID
  createdAt: number; // 创建时间（毫秒时间戳）
  updatedAt: number; // 更新时间（毫秒时间戳）
}

/** 获取订单列表请求参数 */
export interface OrderListRequest {
  orderStatus?: OrderStatus;
  page?: number;
  pageSize?: number;
}

/** 获取订单列表返回数据 */
export interface OrderListData {
  orders: Order[];
  total: number; // 总记录数
  page: number; // 当前页码
  pageSize: number; // 每页大小
  totalPages: number; // 总页数
}

/**
 * 获取订单列表（分页）
 * @param params 查询参数：订单状态筛选、分页信息
 * @returns Promise<ApiResponse<OrderListData>>
 */
export const getOrderList = async (
  params: OrderListRequest = {},
): Promise<ApiResponse<OrderListData>> => {
  try {
    const queryParams = new URLSearchParams({
      ...(params.orderStatus && { orderStatus: params.orderStatus }),
      ...(params.page !== undefined && { page: params.page.toString() }),
      ...(params.pageSize !== undefined && { pageSize: params.pageSize.toString() }),
    });

    // const baseUrl = isInternalDomain()
    //   ? '/api/v1/billing/streamlake/orders'
    //   : '/api/proxy/api/v1/billing/streamlake/orders';
    const baseUrl = '/api/proxy/api/v1/billing/streamlake/orders';

    const response = (await client({
      url: `${baseUrl}?${queryParams.toString()}`,
      method: 'GET',
    })) as ApiResponse<OrderListData>;
    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get order list');
  }
};

/** 取消订阅请求参数 */
export interface CancelSubscriptionRequest {
  planCode?: string; // 套餐代码，不传则自动从用户账户信息获取
}

/** 取消订阅返回数据 */
export interface CancelSubscriptionData {
  code: number;
  msg: string;
  success: boolean;
  userId: string;
  skuId: string;
  planCode: string;
  orderOutNo: string;
}

/**
 * 取消自动订阅
 * @param params 请求参数：套餐代码（可选）
 * @returns Promise<ApiResponse<CancelSubscriptionData>>
 */
export const cancelSubscription = async (
  params: CancelSubscriptionRequest = {},
): Promise<ApiResponse<CancelSubscriptionData>> => {
  try {
    const baseUrl = '/api/proxy/api/v1/billing/streamlake/subscription/cancel';

    const response = (await client({
      url: baseUrl,
      method: 'POST',
      data: params.planCode ? { planCode: params.planCode } : {},
    })) as ApiResponse<CancelSubscriptionData>;
    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to cancel subscription');
  }
};

/** 可升级的套餐信息 */
export interface UpgradablePlan {
  planCode: string; // 套餐代码，
  planName: string; // 套餐名称
  skuId: string; // SKU标识
}

/** 检查升级请求参数 */
export interface CheckUpgradeRequest {
  currentPlanCode?: string; // 当前套餐代码，不传则自动从用户账户信息获取
}

/** 检查升级返回数据 */
export interface CheckUpgradeData {
  code: number;
  msg: string;
  canUpgrade: boolean; // 是否可以升级
  currentPlanCode: string; // 当前套餐代码
  currentPlanName: string; // 当前套餐名称
  currentTier: string; // 当前等级
  upgradablePlans: UpgradablePlan[]; // 可升级的套餐列表
  reason: string | null; // 不可升级时的原因说明
}

/**
 * 检查当前套餐是否可以升级
 * @param params 请求参数：当前套餐代码（可选）
 * @returns Promise<ApiResponse<CheckUpgradeData>>
 */
export const checkUpgrade = async (
  params: CheckUpgradeRequest = {},
): Promise<ApiResponse<CheckUpgradeData>> => {
  try {
    const baseUrl = '/api/proxy/api/v1/billing/streamlake/upgrade/check';

    const response = (await client({
      url: baseUrl,
      method: 'GET',
    })) as ApiResponse<CheckUpgradeData>;
    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to check upgrade');
  }
};
